import React from 'react';
import { withTranslation } from 'react-i18next';
import {
    Progress, Button, FormGroup, Container, Row, Col, Card, CardBody, CardHeader,
    Input as ReactstrapInput, Label,
} from 'reactstrap';
import moment from 'moment';
import i18n from '@s20/i18n';
import { convertWinToIANA, formatOffsetInTimeZone, getTimeZoneAbbr } from '@s20/common/util/date';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes, MomentDateFormat, MomentTimeFormat } from '../Common';
import {
    toasty, Textarea, Input,
} from '../common/forms/FormElements';
import { RadioGroup, Radio } from '../common/forms/Radio';
import { ModalForm } from '../common/forms/ModalForm';
import './TimeTracker.scss';
import 'moment-timezone';

const ModalType = {
    startShift: {
        type: 'startShift',
        titleKey: 'timeTracker:confirmStartShift',
        apiRoute: ApiRoutes.timeTracker.startShift,
        successKey: 'timeTracker:shiftStartedSuccess',
        errorKey: 'timeTracker:failedStartShift',
        isJobAction: false,
    },
    endShift: {
        type: 'endShift',
        titleKey: 'timeTracker:confirmEndShift',
        apiRoute: ApiRoutes.timeTracker.endShift,
        successKey: 'timeTracker:shiftEndedSuccess',
        errorKey: 'timeTracker:failedEndShift',
        isJobAction: false,
    },
    startJob: {
        type: 'startJob',
        titleKey: 'timeTracker:confirmStartJob',
        apiRoute: ApiRoutes.timeTracker.startJob,
        successKey: 'timeTracker:jobStartedSuccess',
        errorKey: 'timeTracker:failedStartJob',
        isJobAction: true,
    },
    endJob: {
        type: 'endJob',
        titleKey: 'timeTracker:confirmEndJob',
        apiRoute: ApiRoutes.timeTracker.endJob,
        successKey: 'timeTracker:jobEndedSuccess',
        errorKey: 'timeTracker:failedEndJob',
        isJobAction: true,
    },
};

const ModalFields = {
    overrideTime: 'overrideTime',
    overrideTimeValue: 'overrideTimeValue',
    notes: 'notes',
};

const getCurrentLocation = async () => {
    if (navigator.geolocation) {
        try {
            const position = await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        resolve({
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                            accuracy: position.coords.accuracy,
                            source: 'browser',
                        });
                    },
                    (error) => {
                        reject(error);
                    },
                    {
                        enableHighAccuracy: true,
                        timeout: 10000,
                        maximumAge: 0,
                    },
                );
            });
            return position;
        } catch {
            return null;
        }
    }
    return null;
};

const getCurrentTimeInTimeZone = (timeZone) => {
    if (!timeZone) {
        return moment().format('HH:mm');
    }

    const ianaTimeZone = convertWinToIANA(timeZone);
    if (ianaTimeZone) {
        return moment.tz(ianaTimeZone).format('HH:mm');
    }
    return moment().format('HH:mm');
};

const getEventTimeInTimeZone = (overrideTimeValue, timeZone) => {
    let ianaTimeZone = timeZone;

    if (timeZone) {
        ianaTimeZone = convertWinToIANA(timeZone);
    }

    if (!ianaTimeZone) {
        return moment(overrideTimeValue, 'HH:mm');
    }

    const [hours, minutes] = overrideTimeValue.split(':').map(Number);
    return moment.tz(ianaTimeZone).set({
        hour: hours, minute: minutes, second: 0, millisecond: 0,
    });
};

const formatTimeWithTimeZone = (dateTime, timeZone) => {
    if (!dateTime) return i18n.t('timeTracker:noTime');
    return formatOffsetInTimeZone(dateTime, MomentDateFormat.DateTimeShortWeekDayAnte, timeZone);
};

const buildJobTooltip = (job) => {
    const parts = [];
    if (job.customerName) {
        parts.push(
            <div key="customer">
                <strong>{i18n.t('timeTracker:customerLabel')}</strong>
                {' '}
                {job.customerName}
            </div>,
        );
    }
    if (job.workOrderNumber) {
        parts.push(
            <div key="wo">
                <strong>{i18n.t('timeTracker:workOrderLabel')}</strong>
                {' '}
                {job.workOrderNumber}
            </div>,
        );
    }
    if (job.jobLocation) {
        parts.push(
            <div key="jobLoc">
                <strong>{i18n.t('timeTracker:jobLocationLabel')}</strong>
                {' '}
                {job.jobLocation}
            </div>,
        );
    }
    if (job.meetingLocation) {
        parts.push(
            <div key="meetingLoc">
                <strong>{i18n.t('timeTracker:meetingLocationLabel')}</strong>
                {' '}
                {job.meetingLocation}
            </div>,
        );
    }

    if (job.trackingStartTime || job.trackingEndTime) {
        parts.push(
            <hr
                key="divider"
                className="time-tracker-divider"
            />,
        );

        if (job.trackingStartTime) {
            const startTime = formatOffsetInTimeZone(job.trackingStartTime, MomentTimeFormat.AnteTwelveHour, job.timeZone);

            parts.push(
                <div key="startTime">
                    <strong>{i18n.t('timeTracker:jobStartedTooltip')}</strong>
                    {' '}
                    {startTime}
                </div>,
            );
        }

        if (job.trackingEndTime) {
            const endTime = formatOffsetInTimeZone(job.trackingEndTime, MomentTimeFormat.AnteTwelveHour, job.timeZone);

            parts.push(
                <div key="endTime">
                    <strong>{i18n.t('timeTracker:jobCompletedTooltip')}</strong>
                    {' '}
                    {endTime}
                </div>,
            );
        }
    }

    return parts.length > 0 ? <>{parts}</> : null;
};

const requestLocationPermission = async () => {
    await getCurrentLocation().catch(() => null);
};

const selectDefaultJobId = (sched) => {
    const jobs = sched?.jobs ?? [];
    if (jobs.length === 0) return null;
    const startedJob = jobs.find((j) => j.trackingStartTime && !j.trackingEndTime);
    if (startedJob) return startedJob.jobId;
    const firstUpcoming = jobs.find((j) => !j.trackingStartTime && !j.trackingEndTime);
    if (firstUpcoming) return firstUpcoming.jobId;
    return jobs[0].jobId;
};
const getNextJob = (jobsList) => {
    if (jobsList.length === 0) return null;

    const sorted = [...jobsList].sort((a, b) => {
        const timeA = a.startTime ? moment(a.startTime) : moment().add(1, 'year');
        const timeB = b.startTime ? moment(b.startTime) : moment().add(1, 'year');
        return timeA.diff(timeB);
    });

    return sorted[0];
};

const getNextJobStartTime = (jobsList) => {
    if (jobsList.length === 0) return null;

    const nextJob = getNextJob(jobsList);
    return nextJob?.startTime ? moment(nextJob.startTime) : null;
};

class TimeTracker extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            schedule: {},
            selectedJobId: null,
            isModalOpen: false,
            modalType: null,
            modalAction: null,
            [ModalFields.overrideTime]: false,
            [ModalFields.overrideTimeValue]: '',
            [ModalFields.notes]: '',
            submitting: false,
        };
    }

    componentDidMount() {
        requestLocationPermission();
        this.populateState();
    }

    getSelectedJob = () => {
        const { schedule, selectedJobId } = this.state;
        const jobs = schedule?.jobs ?? [];
        return jobs.find((j) => j.jobId === selectedJobId);
    };

    populateState = async () => {
        const { t } = this.props;
        try {
            const response = await httpClient.js(ApiRoutes.timeTracker.jobs());
            this.setState({
                schedule: response,
                selectedJobId: selectDefaultJobId(response),
                loading: false,
            });
        } catch {
            toasty.error(t('timeTracker:couldNotLoadJobs'));
            this.setState({ loading: false });
        }
    };

    openModal = (action, type) => {
        const selectedJob = this.getSelectedJob();
        const timeZone = type?.isJobAction ? selectedJob?.timeZone : this.state.schedule?.dispatchLocationTimeZone;
        const defaultTime = getCurrentTimeInTimeZone(timeZone);

        this.setState({
            isModalOpen: true,
            modalType: type,
            modalAction: action,
            [ModalFields.overrideTime]: false,
            [ModalFields.overrideTimeValue]: defaultTime,
            [ModalFields.notes]: '',
            submitting: false,
        });
    };

    closeModal = () => {
        this.setState({
            isModalOpen: false,
            modalType: null,
            modalAction: null,
            [ModalFields.overrideTime]: false,
            [ModalFields.overrideTimeValue]: '',
            [ModalFields.notes]: '',
            submitting: false,
        });
    };

    handleModalFieldChange = (field, value) => {
        if (field === ModalFields.overrideTime && value === false) {
            const { modalType, schedule } = this.state;
            const selectedJob = this.getSelectedJob();
            const timeZone = modalType?.isJobAction ? selectedJob?.timeZone : schedule?.dispatchLocationTimeZone;
            const currentTime = getCurrentTimeInTimeZone(timeZone);

            this.setState({
                [field]: value,
                [ModalFields.overrideTimeValue]: currentTime,
            });
        } else {
            this.setState({ [field]: value });
        }
    };

    handleModalSubmit = async () => {
        const {
            overrideTime,
            overrideTimeValue,
            notes,
            modalType,
            schedule,
            modalAction,
        } = this.state;
        const { t } = this.props;

        if (overrideTime) {
            if (!overrideTimeValue || !overrideTimeValue.trim()) {
                toasty.error(t('timeTracker:overrideTimeRequired'));
                return;
            }
            if (!notes || !notes.trim()) {
                toasty.error(t('timeTracker:notesRequired'));
                return;
            }
        }

        const jobs = schedule?.jobs ?? [];
        const selectedJob = this.getSelectedJob();

        let eventTime;
        if (overrideTime && overrideTimeValue) {
            if (modalType?.isJobAction && selectedJob) {
                eventTime = getEventTimeInTimeZone(overrideTimeValue, selectedJob.timeZone);
            } else {
                eventTime = getEventTimeInTimeZone(overrideTimeValue, schedule?.dispatchLocationTimeZone);
            }
        } else {
            if (modalType?.isJobAction) {
                if (selectedJob?.timeZone) {
                    const ianaTimeZone = convertWinToIANA(selectedJob.timeZone);
                    eventTime = ianaTimeZone ? moment.tz(ianaTimeZone) : moment();
                } else {
                    eventTime = moment();
                }
            } else {
                const dispatchTimeZone = schedule?.dispatchLocationTimeZone;
                if (dispatchTimeZone) {
                    const ianaTimeZone = convertWinToIANA(dispatchTimeZone);
                    eventTime = ianaTimeZone ? moment.tz(ianaTimeZone) : moment();
                } else {
                    eventTime = moment();
                }
            }
        }

        switch (modalType?.type) {
            case ModalType.startJob.type: {
                if (schedule?.shiftStart) {
                    const shiftStartTime = moment(schedule.shiftStart);

                    if (eventTime.isBefore(shiftStartTime, 'minute')) {
                        const eventInTimeZone = formatOffsetInTimeZone(eventTime, MomentTimeFormat.AnteTwelveHour, selectedJob.timeZone);
                        toasty.error(t('timeTracker:jobStartBeforeShift', {
                            eventTime: eventInTimeZone,
                            shiftStart: shiftStartTime.format('h:mm A'),
                        }));
                        return;
                    }
                }
                break;
            }

            case ModalType.endJob.type: {
                if (selectedJob?.trackingStartTime) {
                    const jobStartTime = moment(selectedJob.trackingStartTime);
                    if (eventTime.isBefore(jobStartTime, 'minute')) {
                        const eventInTimeZone = formatOffsetInTimeZone(eventTime, MomentTimeFormat.AnteTwelveHour, selectedJob.timeZone);
                        const startInTimeZone = formatOffsetInTimeZone(jobStartTime, MomentTimeFormat.AnteTwelveHour, selectedJob.timeZone);
                        toasty.error(t('timeTracker:jobEndBeforeStart', {
                            eventTime: eventInTimeZone,
                            startTime: startInTimeZone,
                        }));
                        return;
                    }
                }
                break;
            }

            case ModalType.endShift.type: {
                if (schedule?.shiftStart) {
                    const shiftStartTime = moment(schedule.shiftStart);

                    if (eventTime.isBefore(shiftStartTime, 'minute')) {
                        toasty.error(t('timeTracker:shiftEndBeforeStart', {
                            eventTime: eventTime.format('h:mm A'),
                            shiftStart: shiftStartTime.format('h:mm A'),
                        }));
                        return;
                    }
                }

                const completedJobs = jobs.filter((j) => j.trackingEndTime);
                for (const job of completedJobs) {
                    const jobEndTime = moment(job.trackingEndTime);
                    const jobEndTimeInTimeZone = formatOffsetInTimeZone(jobEndTime, MomentTimeFormat.AnteTwelveHour, job.timeZone);
                    if (eventTime.isBefore(jobEndTime, 'minute')) {
                        toasty.error(t('timeTracker:shiftEndBeforeLatestJobEnd', {
                            eventTime: eventTime.format('h:mm A'),
                            jobEnd: jobEndTimeInTimeZone,
                        }));
                        return;
                    }
                }

                const upcomingJobsList = jobs.filter((j) => !j.trackingStartTime && !j.trackingEndTime);
                const nextJobStart = getNextJobStartTime(upcomingJobsList);
                const nextJob = getNextJob(upcomingJobsList);
                if (nextJobStart) {
                    const oneHourAfterShiftEnd = eventTime.clone().add(1, 'hour');
                    if (nextJobStart.isBefore(oneHourAfterShiftEnd)) {
                        const eventInTimeZone = formatOffsetInTimeZone(eventTime, MomentTimeFormat.AnteTwelveHour, nextJob.timeZone);
                        toasty.error(t('timeTracker:shiftEndTooCloseToNextJob', {
                            eventTime: eventTime.format('h:mm A'),
                            nextJob: eventInTimeZone,
                        }));
                        return;
                    }
                }
                break;
            }

            case ModalType.startShift.type: {
                if (schedule?.shiftEnd) {
                    const previousShiftEndTime = moment(schedule.shiftEnd);
                    if (eventTime.isBefore(previousShiftEndTime, 'minute')) {
                        toasty.error(t('timeTracker:shiftStartBeforePrevious', {
                            eventTime: eventTime.format('h:mm A'),
                            previousEnd: previousShiftEndTime.format('h:mm A'),
                        }));
                        return;
                    }
                }
                break;
            }

            default:
                break;
        }

        this.setState({ submitting: true });
        try {
            const location = await getCurrentLocation().catch(() => null);
            if (modalAction) {
                await modalAction({
                    overrideTime,
                    overrideTimeValue,
                    notes,
                    location,
                });
            }
            this.closeModal();
        } finally {
            this.setState({ submitting: false });
        }
    };

    timeTrackerHandler = (modalTypeConfig) => async (data) => {
        const { t } = this.props;
        const {
            overrideTime: dataOverrideTime,
            overrideTimeValue: dataOverrideTimeValue,
            location,
            notes: dataNotes,
        } = data;

        const {
            isJobAction, apiRoute, successKey, errorKey,
        } = modalTypeConfig;

        const { schedule, selectedJobId } = this.state;
        const jobs = schedule?.jobs ?? [];
        const selectedJob = jobs.find((j) => j.jobId === selectedJobId);

        let eventTime;
        if (dataOverrideTime) {
            if (isJobAction) {
                if (selectedJob) {
                    eventTime = getEventTimeInTimeZone(dataOverrideTimeValue, selectedJob.timeZone).format();
                } else {
                    eventTime = moment(dataOverrideTimeValue, 'HH:mm').format();
                }
            } else {
                eventTime = getEventTimeInTimeZone(dataOverrideTimeValue, schedule?.dispatchLocationTimeZone).format();
            }
        } else {
            if (isJobAction) {
                if (selectedJob?.timeZone) {
                    const ianaTimeZone = convertWinToIANA(selectedJob.timeZone);
                    eventTime = ianaTimeZone ? moment.tz(ianaTimeZone).format() : moment().format();
                } else {
                    eventTime = moment().format();
                }
            } else {
                const dispatchTimeZone = schedule?.dispatchLocationTimeZone;
                if (dispatchTimeZone) {
                    const ianaTimeZone = convertWinToIANA(dispatchTimeZone);
                    eventTime = ianaTimeZone ? moment.tz(ianaTimeZone).format() : moment().format();
                } else {
                    eventTime = moment().format();
                }
            }
        }

        if (isJobAction && !selectedJob) {
            toasty.error(t('timeTracker:noJobSelected'));
            return;
        }

        const payload = {
            eventTime,
            hasOverride: dataOverrideTime,
            notes: dataNotes,
            latitude: location?.latitude,
            longitude: location?.longitude,
            accuracy: location?.accuracy,
            locationSource: location?.source,
        };

        try {
            const url = isJobAction ? apiRoute(selectedJob.timesheetId) : apiRoute();
            const response = await httpClient.post(url, payload);

            this.setState({
                schedule: response,
                selectedJobId: selectDefaultJobId(response),
            });
            toasty.success(t(successKey));
        } catch (error) {
            console.error(t(errorKey), error);
            toasty.error(t(errorKey));
        }
    };

    handleStartShift = this.timeTrackerHandler(ModalType.startShift);

    handleEndShift = this.timeTrackerHandler(ModalType.endShift);

    handleStartJob = this.timeTrackerHandler(ModalType.startJob);

    handleEndJob = this.timeTrackerHandler(ModalType.endJob);

    handleJobChange = (e) => {
        const { value } = e.target;
        this.setState({ selectedJobId: Number(value) });
    };

    getTzAbbreviation = () => {
        const { schedule, overrideTimeValue, modalType } = this.state;

        if (!overrideTimeValue) return '';

        const date = moment().format('YYYY-MM-DD');
        const time = overrideTimeValue;

        if (modalType?.isJobAction) {
            const selectedJob = this.getSelectedJob();

            if (!selectedJob) return '';

            return getTimeZoneAbbr(
                selectedJob.startTimeOffset,
                selectedJob.timeZone,
                date,
                time,
            );
        }

        if (schedule?.dispatchLocationTimeZone) {
            return getTimeZoneAbbr(
                null,
                schedule.dispatchLocationTimeZone,
                date,
                time,
            );
        }

        return '';
    };

    render() {
        const {
            loading,
            schedule,
            selectedJobId,
            isModalOpen,
            modalType,
            overrideTime,
            overrideTimeValue,
            notes,
            submitting,
        } = this.state;
        const { t } = this.props;

        if (loading) {
            return <Progress />;
        }

        const jobs = schedule?.jobs ?? [];
        const shiftStart = schedule?.shiftStart;
        const shiftEnd = schedule?.shiftEnd;
        const selectedJob = this.getSelectedJob();
        const startedJob = jobs.find((j) => j.trackingStartTime && !j.trackingEndTime);
        const upcomingJobs = jobs.filter((j) => !j.trackingStartTime && !j.trackingEndTime);
        const hasUpcomingJobs = upcomingJobs.length > 0;

        const nextJobStart = getNextJobStartTime(upcomingJobs);
        const nextJobStartsWithinHour = nextJobStart ? nextJobStart.isBefore(moment().add(1, 'hour')) : false;

        const ongoingShift = shiftStart && (!shiftEnd || moment(shiftEnd).isBefore(shiftStart));
        const completedShift = shiftEnd && (moment(shiftStart).isBefore(shiftEnd));
        const canStartShift = (!shiftStart || completedShift) && hasUpcomingJobs;
        const canEndShift = ongoingShift && !startedJob && !nextJobStartsWithinHour;
        const canStartJob = ongoingShift && hasUpcomingJobs && !startedJob;
        const canEndJob = ongoingShift && !!selectedJob?.trackingStartTime && !selectedJob?.trackingEndTime;
        const hasJobsToDisplay = (shiftStart && startedJob) || (ongoingShift && upcomingJobs.length > 0 && !startedJob);

        const showNoJobsMessage = !hasUpcomingJobs && !shiftStart && !completedShift;
        const showCompletedMessage = completedShift && !hasUpcomingJobs;
        const showButtons = !showNoJobsMessage && !showCompletedMessage;

        return (
            <Container
                className={`py-5 ${!hasJobsToDisplay ? 'time-tracker-centered' : ''}`}
            >
                <Row className="justify-content-center mb-4">
                    <Col
                        xs="12"
                        sm="10"
                        md="8"
                        lg="6"
                    >
                        <div className="time-tracker-buttons-container mb-3">
                            {showNoJobsMessage && (
                                <div className="w-100 text-center text-muted py-3">
                                    <div className="w-100 text-center text-success font-weight-bold display-4 py-3">
                                        {t('timeTracker:noJobsScheduled')}
                                    </div>
                                </div>
                            )}

                            {showCompletedMessage && (
                                <div className="w-100 text-center text-success font-weight-bold display-4 py-3">
                                    {t('timeTracker:shiftCompleted')}
                                </div>
                            )}

                            {showButtons && (
                                <>
                                    {canStartShift && (
                                        <Button
                                            color="primary"
                                            size="lg"
                                            className="w-100 py-3 h3 mb-0"
                                            disabled={submitting}
                                            onClick={() => this.openModal(this.handleStartShift, ModalType.startShift)}
                                        >
                                            {t('timeTracker:startShift')}
                                        </Button>
                                    )}
                                    {canEndShift && (
                                        <Button
                                            color="danger"
                                            size="lg"
                                            className="w-100 py-3 h3 mb-0"
                                            disabled={submitting}
                                            onClick={() => this.openModal(this.handleEndShift, ModalType.endShift)}
                                        >
                                            {t('timeTracker:endShift')}
                                        </Button>
                                    )}
                                    {canStartJob && (
                                        <Button
                                            color="warning"
                                            size="lg"
                                            className="w-100 py-3 h3 mb-0"
                                            disabled={submitting}
                                            onClick={() => this.openModal(this.handleStartJob, ModalType.startJob)}
                                        >
                                            {t('timeTracker:startJob')}
                                        </Button>
                                    )}
                                    {canEndJob && (
                                        <Button
                                            color="warning"
                                            size="lg"
                                            className="w-100 py-3 h3 mb-0"
                                            disabled={submitting}
                                            onClick={() => this.openModal(this.handleEndJob, ModalType.endJob)}
                                        >
                                            {t('timeTracker:endJob')}
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>

                        {shiftStart && (
                            <div className="text-center">
                                {ongoingShift ? (
                                    <div>
                                        <strong>{t('timeTracker:currentShiftStarted')}</strong>
                                        {' '}
                                        {formatTimeWithTimeZone(shiftStart, schedule?.dispatchLocationTimeZone)}
                                    </div>
                                ) : (
                                    <div>
                                        <div>
                                            <strong>{t('timeTracker:shiftStarted')}</strong>
                                            {' '}
                                            {formatTimeWithTimeZone(shiftStart, schedule?.dispatchLocationTimeZone)}
                                        </div>
                                        <div>
                                            <strong>{t('timeTracker:shiftCompletedLabel')}</strong>
                                            {' '}
                                            {formatTimeWithTimeZone(shiftEnd, schedule?.dispatchLocationTimeZone)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </Col>
                </Row>

                <Row className="justify-content-center">
                    <Col
                        xs="12"
                        sm="10"
                        md="8"
                        lg="6"
                    >
                        {shiftStart && startedJob && (
                            <Card className="mb-3">
                                <CardHeader>
                                    <h5 className="mb-0">{t('timeTracker:currentJob')}</h5>
                                </CardHeader>
                                <CardBody>
                                    <RadioGroup
                                        name="job"
                                        value={selectedJobId}
                                        onChange={this.handleJobChange}
                                        disabled
                                    >
                                        <Radio
                                            key={`started-${startedJob.jobId}`}
                                            value={startedJob.jobId}
                                            label={(
                                                <span className="ml-2">
                                                    <strong>{t('timeTracker:startTimeLabel')}</strong>
                                                    {' '}
                                                    {formatTimeWithTimeZone(startedJob.startTime, startedJob.timeZone)}
                                                </span>
                                            )}
                                            formGroupClassName="mb-2"
                                            tooltip={buildJobTooltip(startedJob)}
                                        />
                                    </RadioGroup>
                                </CardBody>
                            </Card>
                        )}

                        {ongoingShift && upcomingJobs.length > 0 && !startedJob && (
                            <Card>
                                <CardHeader>
                                    <h5 className="mb-0">{t('timeTracker:todaysJobs')}</h5>
                                </CardHeader>
                                <CardBody>
                                    <RadioGroup
                                        name="job"
                                        value={selectedJobId}
                                        onChange={this.handleJobChange}
                                        disabled={!ongoingShift}
                                    >
                                        {upcomingJobs.map((j) => {
                                            const { jobId } = j;
                                            const startTime = formatTimeWithTimeZone(j.startTime, j.timeZone);
                                            const label = (
                                                <span className="ml-2">
                                                    <strong>{t('timeTracker:startTimeLabel')}</strong>
                                                    {' '}
                                                    {startTime}
                                                </span>
                                            );
                                            return (
                                                <Radio
                                                    key={`upcoming-${jobId}`}
                                                    value={jobId}
                                                    label={label}
                                                    formGroupClassName="mb-2"
                                                    tooltip={buildJobTooltip(j)}
                                                />
                                            );
                                        })}
                                    </RadioGroup>
                                </CardBody>
                            </Card>
                        )}

                    </Col>
                </Row>

                <ModalForm
                    isOpen={isModalOpen}
                    toggle={this.closeModal}
                    title={modalType ? t(modalType.titleKey) : ''}
                    onSubmit={this.handleModalSubmit}
                    SaveButtonProps={{ label: t('timeTracker:confirm'), saving: submitting, savingLabel: t('timeTracker:confirming') }}
                >
                    <FormGroup switch>
                        <ReactstrapInput
                            type="switch"
                            id={ModalFields.overrideTime}
                            name={ModalFields.overrideTime}
                            checked={overrideTime}
                            onChange={(e) => this.handleModalFieldChange(ModalFields.overrideTime, e.target.checked)}
                        />
                        <Label
                            check
                            htmlFor={ModalFields.overrideTime}
                        >
                            {t('timeTracker:overrideTime')}
                        </Label>
                    </FormGroup>

                    <Input
                        type="time"
                        label={t('timeTracker:time')}
                        required={overrideTime}
                        id={ModalFields.overrideTimeValue}
                        name={ModalFields.overrideTimeValue}
                        value={overrideTimeValue}
                        onChange={(e) => this.handleModalFieldChange(ModalFields.overrideTimeValue, e.target.value)}
                        disabled={!overrideTime}
                        error={t('timeTracker:timeRequired')}
                        endAddonClassName={this.getTzAbbreviation() ? 'inset' : undefined}
                        endAddon={this.getTzAbbreviation() ? (
                            <span className="text-muted">
                                {this.getTzAbbreviation()}
                            </span>
                        ) : undefined}
                    />

                    <Textarea
                        id={ModalFields.notes}
                        name={ModalFields.notes}
                        label={t('timeTracker:notes')}
                        required={overrideTime}
                        error={t('timeTracker:noteRequired')}
                        value={notes}
                        onChange={(e) => this.handleModalFieldChange(ModalFields.notes, e.target.value)}
                        rows={4}
                    />
                </ModalForm>
            </Container>
        );
    }
}

export default withTranslation()(TimeTracker);
